import type { Language } from '@eformulieren/engine'
import type { ProjectionSection } from '@eformulieren/model'
import { type ReactNode, useId } from 'react'
import { inLanguage } from '../language/text.js'

/**
 * Wie via een deling binnenkwam: van wie, en dat de aanvrager indient (R-69, audit delen G-16, mockup
 * 07, besluiten 1, 5 en 6 van 30 oktober 2026).
 *
 * De pagina weet hoe de kijker binnenkwam (`mijnToegang: 'deling'` in het overzicht van `GET
 * /delingen`) en wie uitnodigde (`delerRol` van zijn eigen regel, die als enige in dat overzicht staat).
 * Als rol en nooit met een adres: wie uitnodigde staat als rol in beeld (besluit 5).
 */
export interface SharedEntry {
  /** Wie de kijker uitnodigde, als rol; niets als het overzicht het niet zegt. */
  readonly uitgenodigdDoor?: 'aanvrager' | 'gemachtigde' | 'medewerker-nwo' | undefined
}

const INVITER: Readonly<Record<NonNullable<SharedEntry['uitgenodigdDoor']>, { nl: string; en: string }>> = {
  aanvrager: { nl: 'de aanvrager', en: 'the applicant' },
  gemachtigde: { nl: 'de gemachtigde', en: 'the authorised representative' },
  'medewerker-nwo': { nl: 'een medewerker van NWO', en: 'an NWO staff member' },
}

const REGION = { nl: 'Uw toegang', en: 'Your access' }
const CLOSE = { nl: 'Sluiten', en: 'Close' }

/** "A", "A en B", "A, B en C". */
function list(names: readonly string[], language: Language): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} ${language === 'nl' ? 'en' : 'and'} ${names[names.length - 1] ?? ''}`
}

/** De zinnen van de banner, los te toetsen. */
function sharedEntryText(
  sections: readonly Pick<ProjectionSection, 'titel' | 'recht' | 'type'>[],
  inviter: SharedEntry['uitgenodigdDoor'],
  language: Language,
): { readonly kop: string; readonly rechten: string; readonly rest: string } {
  const own = sections.filter((section) => section.type !== 'bron')
  const writes = own.filter((section) => section.recht === 'bewerken').map((section) => inLanguage(section.titel, language))
  const reads = own.filter((section) => section.recht !== 'bewerken').map((section) => inLanguage(section.titel, language))
  const by = inviter === undefined ? undefined : inLanguage(INVITER[inviter], language)
  const writing = writes.length > 0
  const kop =
    language === 'nl'
      ? `U ${writing ? 'schrijft' : 'leest'} mee${by === undefined ? '' : ` op uitnodiging van ${by}`}.`
      : `You are ${writing ? 'writing' : 'reading'} along${by === undefined ? '' : ` at the invitation of ${by}`}.`
  const parts =
    language === 'nl'
      ? [writes.length === 0 ? undefined : `schrijven in ${list(writes, language)}`, reads.length === 0 ? undefined : `lezen in ${list(reads, language)}`]
      : [writes.length === 0 ? undefined : `write in ${list(writes, language)}`, reads.length === 0 ? undefined : `read ${list(reads, language)}`]
  const shown = parts.filter((part): part is string => part !== undefined)
  const rechten = shown.length === 0 ? '' : language === 'nl' ? `U kunt ${shown.join(' en ')}.` : `You can ${shown.join(' and ')}.`
  const rest =
    language === 'nl'
      ? `Indienen doet de aanvrager.${writing ? ' Wat u schrijft, wordt automatisch opgeslagen en is herkenbaar als van u.' : ''} Dit staat ook onder Wie heeft toegang.`
      : `The applicant submits.${writing ? ' What you write is saved automatically and recognisable as yours.' : ''} You can find this under Who has access too.`
  return { kop, rechten, rest }
}

export function SharedEntryBanner({
  secties: sections,
  deling: entry,
  taal: language,
  onSluit: onClose,
}: {
  readonly secties: readonly ProjectionSection[]
  readonly deling: SharedEntry
  readonly taal: Language
  readonly onSluit: () => void
}): ReactNode {
  const id = useId()
  const text = sharedEntryText(sections, entry.uitgenodigdDoor, language)
  return (
    <section className="ef-deelbanner" aria-labelledby={id} data-proef="deelbanner">
      <h2 id={id} className="ef-sr-only">
        {inLanguage(REGION, language)}
      </h2>
      <p className="ef-deelbanner__tekst">
        <span className="ef-deelbanner__kop">{text.kop}</span> {text.rechten} {text.rest}
      </p>
      <button type="button" className="ef-deelbanner__sluit" onClick={onClose}>
        {inLanguage(CLOSE, language)}
      </button>
    </section>
  )
}
